import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { incorporadoraService } from "../../services/incorporadoraService";
import {
  Incorporadora,
  IncorporadoraCreate,
  IncorporadoraUpdate,
} from "../../types/incorporadora";

interface IncorporadoraFormProps {
  incorporadora?: Incorporadora;
  isEditing?: boolean;
}

export const IncorporadoraForm: React.FC<IncorporadoraFormProps> = ({
  incorporadora,
  isEditing = false,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<
    Partial<IncorporadoraCreate> | Partial<IncorporadoraUpdate>
  >({
    nome_fantasia: "",
    razao_social: "",
    cnpj: "",
    descricao: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && incorporadora) {
      setFormData({
        nome_fantasia: incorporadora.nome_fantasia,
        razao_social: incorporadora.razao_social,
        cnpj: incorporadora.cnpj,
        descricao: incorporadora.descricao || "",
      });
    }
  }, [incorporadora, isEditing]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing && incorporadora) {
        await incorporadoraService.update(
          incorporadora.id,
          formData as IncorporadoraUpdate
        );
      } else {
        await incorporadoraService.create(formData as IncorporadoraCreate);
      }
      navigate("/incorporadoras");
    } catch (err: any) {
      setError(err.message || "Erro ao salvar incorporadora.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {isEditing ? "Editar Incorporadora" : "Nova Incorporadora"}
      </h1>
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-lg shadow-md"
      >
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <label
            htmlFor="nome_fantasia"
            className="block text-sm font-medium text-gray-700"
          >
            Nome Fantasia
          </label>
          <input
            type="text"
            name="nome_fantasia"
            id="nome_fantasia"
            value={formData.nome_fantasia}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="razao_social"
            className="block text-sm font-medium text-gray-700"
          >
            Razão Social
          </label>
          <input
            type="text"
            name="razao_social"
            id="razao_social"
            value={formData.razao_social}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="cnpj"
            className="block text-sm font-medium text-gray-700"
          >
            CNPJ
          </label>
          <input
            type="text"
            name="cnpj"
            id="cnpj"
            value={formData.cnpj}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="descricao"
            className="block text-sm font-medium text-gray-700"
          >
            Descrição
          </label>
          <textarea
            name="descricao"
            id="descricao"
            value={formData.descricao || ""}
            onChange={handleInputChange}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
          ></textarea>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate("/incorporadoras")}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md mr-2 hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
          >
            {loading ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
};
